/**
 * Vendor Store
 *
 * Manages vendor state for the multi-step onboarding flow, profile management,
 * and admin approval workflows. Persists onboarding progress to localStorage
 * so vendors can resume where they left off. The 4-step onboarding covers:
 * Step 1 (profile + billing), Step 2 (commission terms), Step 3 (venues),
 * and Step 4 (review + submit). Integrates with the real vendor API for
 * backend persistence.
 *
 * @module useVendorStore
 *
 * @example
 * ```tsx
 * import { useVendorStore, selectOnboardingProgress, selectIsVendorApproved } from '@/store/useVendorStore'
 *
 * function VendorDashboard() {
 *   const { loadVendor } = useVendorStore()
 *   const progress = useVendorStore(selectOnboardingProgress)
 *   const isApproved = useVendorStore(selectIsVendorApproved)
 *
 *   useEffect(() => { loadVendor(vendorId) }, [vendorId])
 *
 *   return isApproved
 *     ? <VendorHome />
 *     : <OnboardingWizard progress={progress} />
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {
  Vendor,
  VendorOnboardingStep1,
  VendorOnboardingStep2,
  VendorOnboardingStep3,
  VirtualVenue,
  PhysicalVenue,
  VendorApprovalStatus,
} from '../types'
import type {
  VendorOrder,
  VendorAnalyticsStats,
  VendorMonthlyData,
  VendorTopChallenge,
  VendorEvent,
} from '../api/vendor.api'
import { vendorApi } from '../api'
import { realVendorApi } from '../api/vendor.real.api'

/**
 * Onboarding state
 */
interface OnboardingState {
  step: number
  step1Data: Partial<VendorOnboardingStep1>
  step2Data: Partial<VendorOnboardingStep2>
  step3Data: {
    virtualVenues: VirtualVenue[]
    physicalVenues: PhysicalVenue[]
  }
  isCompleted: boolean
}

/**
 * Vendor store state
 */
interface VendorState {
  vendor: Vendor | null
  isLoading: boolean
  error: string | null
  onboarding: OnboardingState

  // Orders, analytics, events
  orders: VendorOrder[]
  ordersLoading: boolean
  analytics: VendorAnalyticsStats | null
  monthlyData: VendorMonthlyData[]
  topChallenges: VendorTopChallenge[]
  analyticsLoading: boolean
  vendorEvents: VendorEvent[]
  eventsLoading: boolean
}

/**
 * Vendor store actions
 */
interface VendorActions {
  // Vendor data
  loadVendor: (vendorId: string) => Promise<void>
  setVendor: (vendor: Vendor | null) => void
  clearVendor: () => void

  // Profile updates
  updateProfile: (vendorId: string, data: Partial<Vendor>) => Promise<boolean>
  uploadLogo: (vendorId: string, file: File) => Promise<boolean>

  // Onboarding
  setOnboardingStep: (step: number) => void
  updateStep1: (data: Partial<VendorOnboardingStep1>) => void
  updateStep2: (data: Partial<VendorOnboardingStep2>) => void
  addVirtualVenue: (venue: VirtualVenue) => void
  removeVirtualVenue: (id: string) => void
  addPhysicalVenue: (venue: PhysicalVenue) => void
  removePhysicalVenue: (id: string) => void
  submitStep1: (vendorId: string) => Promise<boolean>
  submitStep2: (vendorId: string) => Promise<boolean>
  submitStep3: (vendorId: string) => Promise<boolean>
  completeOnboarding: (vendorId: string) => Promise<boolean>
  resetOnboarding: () => void

  // Orders
  fetchOrders: (vendorId: string) => Promise<void>
  updateOrderStatus: (orderId: string, status: VendorOrder['status']) => Promise<boolean>

  // Analytics
  fetchAnalytics: (vendorId: string) => Promise<void>

  // Events
  fetchVendorEvents: (vendorId: string) => Promise<void>
  createEvent: (vendorId: string, data: Omit<VendorEvent, 'id' | 'vendorId' | 'createdAt' | 'updatedAt'>) => Promise<boolean>
  updateEvent: (eventId: string, data: Partial<VendorEvent>) => Promise<boolean>
  deleteEvent: (eventId: string) => Promise<boolean>

  // Error handling
  setError: (error: string | null) => void
  clearError: () => void
}

type VendorStore = VendorState & VendorActions

/**
 * Initial onboarding state
 */
const initialOnboarding: OnboardingState = {
  step: 1,
  step1Data: {},
  step2Data: {},
  step3Data: {
    virtualVenues: [],
    physicalVenues: [],
  },
  isCompleted: false,
}

/**
 * Initial state
 */
const initialState: VendorState = {
  vendor: null,
  isLoading: false,
  error: null,
  onboarding: initialOnboarding,
  orders: [],
  ordersLoading: false,
  analytics: null,
  monthlyData: [],
  topChallenges: [],
  analyticsLoading: false,
  vendorEvents: [],
  eventsLoading: false,
}

/**
 * Vendor store
 */
export const useVendorStore = createWithEqualityFn<VendorStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ========================================================================
      // Vendor Data
      // ========================================================================

      loadVendor: async (vendorId: string) => {
        set({ isLoading: true, error: null })

        const response = await realVendorApi.getVendor(vendorId)

        if (response.success) {
          set({
            vendor: response.data,
            isLoading: false,
            onboarding: {
              ...get().onboarding,
              step: response.data.onboardingStep,
              isCompleted: response.data.isOnboardingComplete,
            },
          })
        } else {
          set({
            isLoading: false,
            error: response.error || 'Failed to load vendor',
          })
        }
      },

      setVendor: (vendor) => {
        set({ vendor })
      },

      clearVendor: () => {
        set({ vendor: null, onboarding: initialOnboarding })
      },

      // ========================================================================
      // Profile Updates
      // ========================================================================

      updateProfile: async (vendorId: string, data: Partial<Vendor>) => {
        set({ isLoading: true, error: null })

        const response = await realVendorApi.updateProfile(vendorId, data)

        if (response.success) {
          set({ vendor: response.data, isLoading: false })
          return true
        } else {
          set({
            isLoading: false,
            error: response.error || 'Failed to update profile',
          })
          return false
        }
      },

      uploadLogo: async (vendorId: string, file: File) => {
        set({ isLoading: true, error: null })

        const response = await realVendorApi.uploadLogo(vendorId, file)

        if (response.success && response.data) {
          // Update vendor logo in state
          const vendor = get().vendor
          if (vendor) {
            set({
              vendor: {
                ...vendor,
                profile: { ...vendor.profile, logo: response.data.logoUrl },
              },
              isLoading: false,
            })
          } else {
            set({ isLoading: false })
          }
          return true
        } else {
          set({
            isLoading: false,
            error: response.error || 'Failed to upload logo',
          })
          return false
        }
      },

      // ========================================================================
      // Onboarding
      // ========================================================================

      setOnboardingStep: (step) => {
        set((state) => ({
          onboarding: { ...state.onboarding, step },
        }))
      },

      updateStep1: (data) => {
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            step1Data: { ...state.onboarding.step1Data, ...data },
          },
        }))
      },

      updateStep2: (data) => {
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            step2Data: { ...state.onboarding.step2Data, ...data },
          },
        }))
      },

      addVirtualVenue: (venue) => {
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            step3Data: {
              ...state.onboarding.step3Data,
              virtualVenues: [...state.onboarding.step3Data.virtualVenues, venue],
            },
          },
        }))
      },

      removeVirtualVenue: (id) => {
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            step3Data: {
              ...state.onboarding.step3Data,
              virtualVenues: state.onboarding.step3Data.virtualVenues.filter((v) => v.id !== id),
            },
          },
        }))
      },

      addPhysicalVenue: (venue) => {
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            step3Data: {
              ...state.onboarding.step3Data,
              physicalVenues: [...state.onboarding.step3Data.physicalVenues, venue],
            },
          },
        }))
      },

      removePhysicalVenue: (id) => {
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            step3Data: {
              ...state.onboarding.step3Data,
              physicalVenues: state.onboarding.step3Data.physicalVenues.filter((v) => v.id !== id),
            },
          },
        }))
      },

      submitStep1: async (vendorId) => {
        set({ isLoading: true, error: null })

        const { step1Data } = get().onboarding
        const formData = new FormData()

        // Add logo if present
        if (step1Data.logo instanceof File) {
          formData.append('logo', step1Data.logo)
        }

        // Add other fields
        if (step1Data.tagline) formData.append('tagline', step1Data.tagline)
        if (step1Data.shortDescription) formData.append('shortDescription', step1Data.shortDescription)
        if (step1Data.contactName) formData.append('contactName', step1Data.contactName)
        if (step1Data.phoneNumber) formData.append('phoneNumber', step1Data.phoneNumber)

        // Add social links
        if (step1Data.socialLinks) {
          formData.append('socialLinks', JSON.stringify(step1Data.socialLinks))
        }

        // Add billing address
        if (step1Data.billingAddress) {
          formData.append('billingAddress', JSON.stringify(step1Data.billingAddress))
        }

        // Add tax info
        if (step1Data.ssn) formData.append('ssn', step1Data.ssn)
        if (step1Data.ein) formData.append('ein', step1Data.ein)

        const response = await realVendorApi.submitStep1(vendorId, formData)

        if (response.success) {
          set((state) => ({
            isLoading: false,
            onboarding: { ...state.onboarding, step: 2 },
          }))
          return true
        } else {
          set({
            isLoading: false,
            error: response.error || 'Failed to save step 1',
          })
          return false
        }
      },

      submitStep2: async (vendorId) => {
        set({ isLoading: true, error: null })

        const { step2Data } = get().onboarding

        const response = await realVendorApi.submitStep2(vendorId, step2Data as VendorOnboardingStep2)

        if (response.success) {
          set((state) => ({
            isLoading: false,
            onboarding: { ...state.onboarding, step: 3 },
          }))
          return true
        } else {
          set({
            isLoading: false,
            error: response.error || 'Failed to save step 2',
          })
          return false
        }
      },

      submitStep3: async (vendorId) => {
        set({ isLoading: true, error: null })

        const { step3Data } = get().onboarding

        const response = await realVendorApi.submitStep3(vendorId, step3Data as VendorOnboardingStep3)

        if (response.success) {
          set((state) => ({
            isLoading: false,
            vendor: response.data,
            onboarding: { ...state.onboarding, step: 4 },
          }))
          return true
        } else {
          set({
            isLoading: false,
            error: response.error || 'Failed to save step 3',
          })
          return false
        }
      },

      completeOnboarding: async (vendorId) => {
        set({ isLoading: true, error: null })

        const response = await realVendorApi.completeOnboarding(vendorId)

        if (response.success) {
          set((state) => ({
            isLoading: false,
            vendor: response.data,
            onboarding: { ...state.onboarding, isCompleted: true },
          }))
          return true
        } else {
          set({
            isLoading: false,
            error: response.error || 'Failed to complete onboarding',
          })
          return false
        }
      },

      resetOnboarding: () => {
        set({ onboarding: initialOnboarding })
      },

      // ========================================================================
      // Orders
      // ========================================================================

      fetchOrders: async (vendorId: string) => {
        set({ ordersLoading: true })
        const response = await vendorApi.getOrders(vendorId)
        if (response.success) {
          set({ orders: response.data as VendorOrder[], ordersLoading: false })
        } else {
          set({ ordersLoading: false, error: response.error || 'Failed to load orders' })
        }
      },

      updateOrderStatus: async (orderId: string, status: VendorOrder['status']) => {
        const response = await vendorApi.updateOrderStatus(orderId, status)
        if (response.success) {
          set((state) => ({
            orders: state.orders.map((o) =>
              o.id === orderId ? { ...o, status } : o
            ),
          }))
          return true
        }
        return false
      },

      // ========================================================================
      // Analytics
      // ========================================================================

      fetchAnalytics: async (vendorId: string) => {
        set({ analyticsLoading: true })
        const [statsRes, monthlyRes, topRes] = await Promise.all([
          vendorApi.getAnalytics(vendorId),
          vendorApi.getMonthlyData(vendorId),
          vendorApi.getTopChallenges(vendorId),
        ])
        set({
          analytics: statsRes.success ? (statsRes.data as VendorAnalyticsStats) : null,
          monthlyData: monthlyRes.success ? (monthlyRes.data as VendorMonthlyData[]) : [],
          topChallenges: topRes.success ? (topRes.data as VendorTopChallenge[]) : [],
          analyticsLoading: false,
        })
      },

      // ========================================================================
      // Events
      // ========================================================================

      fetchVendorEvents: async (vendorId: string) => {
        set({ eventsLoading: true })
        const response = await vendorApi.getVendorEvents(vendorId)
        if (response.success) {
          set({ vendorEvents: response.data as VendorEvent[], eventsLoading: false })
        } else {
          set({ eventsLoading: false, error: response.error || 'Failed to load events' })
        }
      },

      createEvent: async (vendorId, data) => {
        const response = await vendorApi.createEvent(vendorId, data)
        if (response.success) {
          set((state) => ({
            vendorEvents: [response.data as VendorEvent, ...state.vendorEvents],
          }))
          return true
        }
        return false
      },

      updateEvent: async (eventId, data) => {
        const response = await vendorApi.updateEvent(eventId, data)
        if (response.success) {
          set((state) => ({
            vendorEvents: state.vendorEvents.map((e) =>
              e.id === eventId ? { ...e, ...data } : e
            ),
          }))
          return true
        }
        return false
      },

      deleteEvent: async (eventId) => {
        const response = await vendorApi.deleteEvent(eventId)
        if (response.success) {
          set((state) => ({
            vendorEvents: state.vendorEvents.filter((e) => e.id !== eventId),
          }))
          return true
        }
        return false
      },

      // ========================================================================
      // Error Handling
      // ========================================================================

      setError: (error) => {
        set({ error })
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'docurious-vendor',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        onboarding: state.onboarding,
      }),
    },
  ),
  shallow,
)

// ============================================================================
// Selectors
// ============================================================================

/**
 * Select the vendor's current approval status.
 *
 * @returns The approval status or `null` if no vendor is loaded
 */
export const selectVendorApprovalStatus = (state: VendorStore): VendorApprovalStatus | null =>
  state.vendor?.approvalStatus ?? null

/** Check if the vendor has been approved to create challenges and receive payouts. */
export const selectIsVendorApproved = (state: VendorStore): boolean =>
  state.vendor?.approvalStatus === 'approved'

/** Check if the vendor's application needs modifications before re-review. */
export const selectNeedsModification = (state: VendorStore): boolean =>
  state.vendor?.approvalStatus === 'awaiting_modification'

/** Check if the vendor needs to sign the platform agreement. */
export const selectNeedsAgreement = (state: VendorStore): boolean =>
  state.vendor?.approvalStatus === 'awaiting_agreement'

/**
 * Select onboarding progress as a percentage (0-100).
 *
 * @returns Percentage of onboarding completion (25% per step, 100% when done)
 */
export const selectOnboardingProgress = (state: VendorStore): number => {
  const { step, isCompleted } = state.onboarding
  if (isCompleted) return 100
  return Math.round((step / 4) * 100)
}

export const selectVendorOrders = (state: VendorStore) => state.orders
export const selectVendorAnalytics = (state: VendorStore) => state.analytics
export const selectVendorMonthlyData = (state: VendorStore) => state.monthlyData
export const selectVendorTopChallenges = (state: VendorStore) => state.topChallenges
export const selectVendorEvents = (state: VendorStore) => state.vendorEvents
